/* eslint-disable no-unsafe-optional-chaining */
import { faChevronDown, faChevronRight, faMultiply } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { chain, filter, keys, map, pickBy, sortBy } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { Field, FormSpy, useForm } from "react-final-form";
import { Badge, Button, Table } from "reactstrap";

import { DollarPriceInput } from "resbutler-ui/components/Form/DollarPriceInput";
import { Card, CardBody } from "resbutler-ui/components/WizElements/Elements";
import { MoneyUtils } from "resbutler-utils/utils/MoneyUtils";
import { Menus } from "../../resbutler-utils/types/Menu";
import { Product, ProductSizes, ProductStock } from "../../resbutler-utils/types/product";
import { OverrideStockForm } from "./OverrideStockForm";

type StockItemFormProps = {
  product: Product;
  menus: Menus;
  restaurantId: string;
  productSizes: ProductSizes;
  date: number;
};

const StockItemForm: React.FC<StockItemFormProps> = ({ product, menus, restaurantId, productSizes, date }) => {
  const [collapse, setCollapse] = useState<Record<string, boolean>>({});
  const form = useForm();
  const stockTypeKey = "specials";

  const toggleCollapse = useCallback((id: string) => {
    setCollapse((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const enabledProductSizes = useMemo(() => {
    if (!product.sizes) return [];
    return chain(product.sizes)
      .pickBy((enabled, sizeId) => enabled && productSizes[sizeId])
      .map((_, sizeId) => sizeId)
      .value();
  }, [product.sizes, productSizes]);

  const getMenuHeadingOptions = (menuId: string, menus: Menus) => {
    const groupHeadings = Object.assign({}, menus[menuId]?.food?.groupHeadings, menus[menuId]?.beverage?.groupHeadings);

    const arrowChar = "→";

    const parseGroup = (groupHeadings, parentHierarchy = "") => {
      const data = [];

      keys(groupHeadings).forEach((key) => {
        const groupHeading = groupHeadings[key];
        const label = parentHierarchy ? `${parentHierarchy} ${arrowChar} ${groupHeading.displayName}` : groupHeading.displayName;

        const hasChildren = keys(groupHeading.groupHeadings).length > 0;

        if (hasChildren) {
          data.push(...parseGroup(groupHeading.groupHeadings, label));
        } else {
          data.push({ label, id: key });
        }
      });

      return data;
    };

    return parseGroup(groupHeadings);
  };

  const addMenuRow = (
    enabledMenusValues: Record<
      string,
      {
        enabled: boolean;
        groupHeadingId?: string;
      }
    >
  ) => {
    const enabledMenus = keys(pickBy(enabledMenusValues || {}, (v) => v.enabled));
    const remainingMenus = Object.keys(menus).filter((m) => !enabledMenus.includes(m));
    if (!remainingMenus.length) return;

    const newMenuId = remainingMenus[0];
    form.change(`${stockTypeKey}.${product.id}.enabledMenus.${newMenuId}`, {
      groupHeadingId: "",
      enabled: true,
    });
  };

  const removeMenuRow = (menuId: string) => {
    form.change(`${stockTypeKey}.${product.id}.enabledMenus.${menuId}.enabled`, false);
  };

  return (
    <FormSpy subscription={{ values: true }}>
      {({ values }) => {
        const productStockValues: ProductStock = values?.[stockTypeKey]?.[product.id];
        const menuOptions = Object.entries(menus).map(([menuId, menu]) => ({
          _key: menuId,
          title: menu.name,
          disabled: productStockValues?.enabledMenus?.[menuId]?.enabled,
        }));

        return (
          <>
            <tr>
              <td>
                <Button type="button" className="me-2" onClick={() => toggleCollapse(product.id)}>
                  <FontAwesomeIcon icon={collapse[product.id] ? faChevronDown : faChevronRight} />
                </Button>

                {product.name}

                <Badge color="info" className="ms-1">
                  {filter(productStockValues?.sizes, (v) => v.enabled).length > 0 ? "override" : ""}
                </Badge>
              </td>
            </tr>

            {collapse[product.id] && (
              <tr>
                <td>
                  <Card>
                    <CardBody>
                      <div className="fw-bold mt-2 mb-2 h4">MENU PLACEMENT</div>
                      <Table size="sm">
                        <thead>
                          <tr>
                            <th>Menu</th>
                            <th>Menu Heading</th>
                          </tr>
                        </thead>
                        <tbody>
                          {map(
                            pickBy(productStockValues.enabledMenus, (v) => v.enabled),
                            (_, menuId) => {
                              const rowField = `${stockTypeKey}.${product.id}.enabledMenus.${menuId}`;
                              const menuHeadingOptions = getMenuHeadingOptions(menuId, menus);

                              return (
                                <tr key={`menu-placement-menu-row-${menuId}`} style={{ "--bs-table-accent-bg": "transparent" } as React.CSSProperties}>
                                  <td>
                                    <select
                                      className="form-select"
                                      value={menuId}
                                      onChange={(e) => {
                                        const selectedMenuId = e.target.value;
                                        form.change(`${stockTypeKey}.${product.id}.enabledMenus`, {
                                          ...(productStockValues?.enabledMenus || {}), //
                                          [menuId]: { ...(productStockValues?.enabledMenus?.[menuId] || {}), enabled: false },
                                          [selectedMenuId]: { ...(productStockValues?.enabledMenus?.[selectedMenuId] || {}), enabled: true },
                                        });
                                      }}
                                    >
                                      {menuOptions?.map((option) => (
                                        <option key={`${menuId}-menu-options`} value={option._key} disabled={option.disabled} className={`${option.disabled ? "text-muted opacity-50" : ""}`}>
                                          {option.title}
                                        </option>
                                      ))}
                                    </select>
                                  </td>

                                  <td>
                                    <Field name={`${rowField}.groupHeadingId`}>
                                      {({ input, meta }) => (
                                        <>
                                          <select className="form-select" value={input.value} onChange={(e) => input.onChange(e.target.value)}>
                                            <option value={""}>Please Select</option>
                                            {menuHeadingOptions.map((mh) => (
                                              <option key={`${menuId}-menuheading-option${mh.id}`} value={mh.id}>
                                                {mh.label}
                                              </option>
                                            ))}
                                          </select>

                                          {meta.touched && meta.error && <span className="validate-form"> {meta.error}</span>}
                                        </>
                                      )}
                                    </Field>
                                  </td>

                                  <td>
                                    <Button className="border border-1" size="sm" onClick={() => removeMenuRow(menuId)}>
                                      <FontAwesomeIcon icon={faMultiply} />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            }
                          )}
                          {/* Add Menu Row */}
                          <tr style={{ "--bs-table-accent-bg": "transparent" } as React.CSSProperties}>
                            <td colSpan={3}>
                              <Button className="border border-1" size="sm" onClick={() => addMenuRow(productStockValues.enabledMenus)}>
                                + Add to another menu
                              </Button>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                      <div className="fw-bold mt-5 mb-2 h4">PRICE</div>
                      <Table size="sm">
                        <thead>
                          <tr>
                            <th>Size</th>
                            <th>Badge Text</th>
                            <th>Price</th>
                            <th>Override Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {map(enabledProductSizes, (sizeId) => {
                            const size = productSizes[sizeId];
                            const sizeData = productStockValues?.sizes?.[sizeId];
                            if (!sizeData) return null;
                            const fieldKey = `specials.${product.id}.sizes.${sizeId}`;
                            return (
                              <React.Fragment key={`price-stock-size-${product.id}-${sizeId}`}>
                                <tr style={{ "--bs-table-accent-bg": "transparent" } as React.CSSProperties}>
                                  <td className={`fw-bold`}>
                                    <div className="d-flex ">{size.name}</div>
                                  </td>
                                  <td>
                                    <Field name={`${fieldKey}.badgeText`}>
                                      {({ input }) => (
                                        <>
                                          <input type="text" value={input.value} className="form-control" onChange={(e) => input.onChange(e.target.value)} />
                                        </>
                                      )}
                                    </Field>
                                  </td>
                                  <td>{MoneyUtils.format(product.restaurants[restaurantId].price?.[size.id])}</td>{" "}
                                  <td>
                                    <Field name={`${fieldKey}.price`}>
                                      {({ input }) => (
                                        <>
                                          <DollarPriceInput type="text" value={input.value} className="form-control" onChange={(e) => input.onChange(e)} />
                                        </>
                                      )}
                                    </Field>
                                  </td>
                                </tr>
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </Table>
                      <div className="fw-bold mt-5 mb-2 h4">OVERRIDE STOCK</div>
                      <OverrideStockForm date={date} restaurantId={restaurantId} enabledProductSizes={enabledProductSizes} productSizes={productSizes} product={product} stockTypeKey={stockTypeKey} />
                    </CardBody>
                  </Card>
                </td>
              </tr>
            )}
          </>
        );
      }}
    </FormSpy>
  );
};

interface StockProductFormProps {
  menus: Menus;
  restaurantId: string;
  productSizes: ProductSizes;
  filteredProducts: Product[];
  date: number;
}

const SpecialStockProductForm: React.FC<StockProductFormProps> = ({ menus, restaurantId, productSizes, filteredProducts, date }) => {
  const sortedProducts = useMemo(() => sortBy(filteredProducts, (p) => p.name), [filteredProducts]);

  return (
    <Table bordered>
      <tbody>
        {sortedProducts.map((product) => (
          <StockItemForm key={`menu-item-stock-override-specials-${product.id}`} product={product} menus={menus} restaurantId={restaurantId} productSizes={productSizes} date={date} />
        ))}
      </tbody>
    </Table>
  );
};

export default SpecialStockProductForm;
